import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import { CodeEditor } from '../common/CodeEditor';
import { toolService } from '../../services/toolService';
import { ToolExecutionResult } from './ToolExecutionResult';
import type { ToolExecutionResult as ToolExecutionResultType, ToolImpactPreview } from '../../types/tool';

export interface ToolExecutionDialogProps {
  open: boolean;
  onClose: () => void;
  toolName: string;
}

/**
 * Dialog for executing a tool with JSON parameters.
 * (Basic implementation: parameters are entered as raw JSON.)
 */
export const ToolExecutionDialog = ({ open, onClose, toolName }: ToolExecutionDialogProps) => {
  const [parametersJson, setParametersJson] = useState<string>('{}');
  const [reason, setReason] = useState<string>('');
  const [isDryRun, setIsDryRun] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ToolExecutionResultType | null>(null);

  const parseParameters = (): Record<string, any> => {
    const trimmed = parametersJson.trim();
    if (!trimmed) return {};
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    throw new Error('Parameters must be a JSON object');
  };

  const handlePreview = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const parameters = parseParameters();
      const preview: ToolImpactPreview = await toolService.previewTool(toolName, parameters);
      setResult({
        toolName,
        isSuccess: true,
        isDryRun: true,
        result: preview.impactPreviewJson,
        impactPreviewJson: preview.impactPreviewJson,
      });
    } catch (err: any) {
      setError(err?.message || err.response?.data?.message || 'Failed to preview tool');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const parameters = parseParameters();
      const exec = await toolService.executeTool(toolName, parameters, isDryRun, reason || undefined);
      setResult(exec);
    } catch (err: any) {
      setError(err?.message || err.response?.data?.message || 'Failed to execute tool');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Execute Tool: {toolName}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <TextField
            label="Reason (optional)"
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={isDryRun} onChange={(e) => setIsDryRun(e.target.checked)} />}
            label="Dry-run (no changes)"
          />
        </Box>

        <Box sx={{ mb: 1 }}>
          <CodeEditor value={parametersJson} onChange={setParametersJson} language="json" height="200px" />
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        <ToolExecutionResult result={result} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={handlePreview} disabled={loading} variant="outlined">
          Preview
        </Button>
        <Button onClick={handleExecute} disabled={loading} variant="contained">
          Execute
        </Button>
      </DialogActions>
    </Dialog>
  );
};

